/**
 * Marketing Dashboard Page - STORY-049
 *
 * Main marketing dashboard with:
 * - Marketing statistics overview
 * - Campaign list with filters
 * - Segment management
 * - Quick actions
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Mail,
  Send,
  Users,
  TrendingUp,
  TrendingDown,
  Plus,
  Search,
  Filter,
  MoreHorizontal,
  Eye,
  Edit2,
  Trash2,
  Copy,
  Calendar,
  Pause,
  Play,
  XCircle,
  AlertCircle,
  BarChart3,
  Target,
  UserMinus,
  Clock,
} from 'lucide-react'
import { Card, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Badge } from '@/components/ui/Badge'
import { Modal } from '@/components/ui/Modal'
import { Spinner } from '@/components/ui/Spinner'
import { EmptyState } from '@/components/ui/EmptyState'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs'
import {
  useCampaigns,
  useSegments,
  useMarketingStats,
  useDeleteCampaign,
  useDuplicateCampaign,
  usePauseCampaign,
  useCancelCampaign,
  useSendCampaign,
  useDeleteSegment,
} from '@/hooks/useMarketing'
import type {
  Campaign,
  CampaignStatus,
  CampaignType,
  Segment,
  SegmentType,
} from '@/types/marketing'
import {
  CAMPAIGN_STATUS_LABELS,
  CAMPAIGN_TYPE_LABELS,
  SEGMENT_TYPE_LABELS,
} from '@/types/marketing'
import { formatDate } from '@/lib/utils'

// Status badge colors
const statusColors: Record<CampaignStatus, 'mint' | 'gold' | 'blue' | 'gray' | 'red'> = {
  draft: 'gray',
  scheduled: 'blue',
  sending: 'gold',
  sent: 'mint',
  paused: 'gold',
  cancelled: 'red',
}

// Segment type colors
const segmentTypeColors: Record<SegmentType, string> = {
  all: 'bg-mint-100 text-mint-800',
  clients: 'bg-blue-100 text-blue-800',
  prospects: 'bg-gold-100 text-gold-800',
  inactive: 'bg-gray-100 text-gray-800',
  custom: 'bg-purple-100 text-purple-800',
}

export default function MarketingPage() {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<'campaigns' | 'segments'>('campaigns')
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<CampaignStatus | 'all'>('all')
  const [typeFilter, setTypeFilter] = useState<CampaignType | 'all'>('all')
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null)
  const [selectedSegment, setSelectedSegment] = useState<Segment | null>(null)
  const [showSegmentDeleteModal, setShowSegmentDeleteModal] = useState(false)

  // Queries
  const { data: stats, isLoading: statsLoading } = useMarketingStats()
  const { data: campaignsData, isLoading: campaignsLoading } = useCampaigns({
    status: statusFilter !== 'all' ? statusFilter : undefined,
    type: typeFilter !== 'all' ? typeFilter : undefined,
    search: searchQuery || undefined,
  })
  const { data: segments, isLoading: segmentsLoading } = useSegments()

  // Mutations
  const deleteCampaignMutation = useDeleteCampaign()
  const duplicateCampaignMutation = useDuplicateCampaign()
  const pauseCampaignMutation = usePauseCampaign()
  const cancelCampaignMutation = useCancelCampaign()
  const sendCampaignMutation = useSendCampaign()
  const deleteSegmentMutation = useDeleteSegment()

  const handleDeleteCampaign = async () => {
    if (!selectedCampaign) return
    await deleteCampaignMutation.mutateAsync(selectedCampaign.id)
    setShowDeleteModal(false)
    setSelectedCampaign(null)
  }

  const handleDuplicateCampaign = async (campaign: Campaign) => {
    await duplicateCampaignMutation.mutateAsync(campaign.id)
  }

  const handlePauseCampaign = async (campaign: Campaign) => {
    await pauseCampaignMutation.mutateAsync(campaign.id)
  }

  const handleCancelCampaign = async (campaign: Campaign) => {
    await cancelCampaignMutation.mutateAsync(campaign.id)
  }

  const handleSendCampaign = async (campaign: Campaign) => {
    await sendCampaignMutation.mutateAsync(campaign.id)
  }

  const handleDeleteSegment = async () => {
    if (!selectedSegment) return
    await deleteSegmentMutation.mutateAsync(selectedSegment.id)
    setShowSegmentDeleteModal(false)
    setSelectedSegment(null)
  }

  const openDeleteModal = (campaign: Campaign) => {
    setSelectedCampaign(campaign)
    setShowDeleteModal(true)
  }

  const openSegmentDeleteModal = (segment: Segment) => {
    setSelectedSegment(segment)
    setShowSegmentDeleteModal(true)
  }

  if (statsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-bold text-brown-900">Marketing</h1>
          <p className="text-brown-600 mt-1">
            Gerez vos campagnes newsletters et segments de contacts
          </p>
        </div>
        <Button
          leftIcon={<Plus className="w-4 h-4" />}
          onClick={() => navigate('/marketing/newsletter/new')}
        >
          Nouvelle campagne
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-brown-500">Campagnes</p>
              <p className="text-2xl font-bold text-brown-900">{stats?.total_campaigns || 0}</p>
              <p className="text-xs text-brown-500 mt-1">
                {stats?.scheduled_campaigns || 0} planifiees
              </p>
            </div>
            <div className="w-12 h-12 rounded-full bg-mint-100 flex items-center justify-center">
              <Mail className="w-6 h-6 text-mint-600" />
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-brown-500">Abonnes actifs</p>
              <p className="text-2xl font-bold text-brown-900">{stats?.active_subscribers || 0}</p>
              <div className="flex items-center gap-1 mt-1">
                {(stats?.subscriber_growth_rate || 0) >= 0 ? (
                  <TrendingUp className="w-3 h-3 text-green-500" />
                ) : (
                  <TrendingDown className="w-3 h-3 text-red-500" />
                )}
                <span
                  className={`text-xs ${
                    (stats?.subscriber_growth_rate || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}
                >
                  {(stats?.subscriber_growth_rate || 0).toFixed(1)}% ce mois
                </span>
              </div>
            </div>
            <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-brown-500">Taux d'ouverture</p>
              <p className="text-2xl font-bold text-brown-900">
                {(stats?.avg_open_rate || 0).toFixed(1)}%
              </p>
              <p className="text-xs text-brown-500 mt-1">Moyenne 30 jours</p>
            </div>
            <div className="w-12 h-12 rounded-full bg-gold-100 flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-gold-600" />
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-brown-500">Desinscriptions</p>
              <p className="text-2xl font-bold text-brown-900">{stats?.unsubscribed_count || 0}</p>
              <p className="text-xs text-brown-500 mt-1">
                {(stats?.avg_unsubscribe_rate || 0).toFixed(2)}% taux moyen
              </p>
            </div>
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
              <UserMinus className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'campaigns' | 'segments')}>
        <TabsList>
          <TabsTrigger value="campaigns">Campagnes</TabsTrigger>
          <TabsTrigger value="segments">Segments</TabsTrigger>
        </TabsList>

        {/* Campaigns Tab */}
        <TabsContent value="campaigns">
          {/* Filters */}
          <Card className="mb-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <Input
                  placeholder="Rechercher une campagne..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  leftIcon={<Search className="w-4 h-4" />}
                />
              </div>
              <div className="flex gap-2 flex-wrap">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as CampaignStatus | 'all')}
                  className="px-3 py-2 border border-beige-300 rounded-lg text-sm focus:ring-2 focus:ring-mint-500"
                >
                  <option value="all">Tous les statuts</option>
                  {Object.entries(CAMPAIGN_STATUS_LABELS).map(([status, label]) => (
                    <option key={status} value={status}>{label}</option>
                  ))}
                </select>
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value as CampaignType | 'all')}
                  className="px-3 py-2 border border-beige-300 rounded-lg text-sm focus:ring-2 focus:ring-mint-500"
                >
                  <option value="all">Tous les types</option>
                  {Object.entries(CAMPAIGN_TYPE_LABELS).map(([type, label]) => (
                    <option key={type} value={type}>{label}</option>
                  ))}
                </select>
              </div>
            </div>
          </Card>

          {/* Campaigns list */}
          {campaignsLoading ? (
            <div className="flex items-center justify-center h-64">
              <Spinner size="lg" />
            </div>
          ) : campaignsData?.campaigns && campaignsData.campaigns.length > 0 ? (
            <div className="space-y-4">
              <AnimatePresence>
                {campaignsData.campaigns.map((campaign) => (
                  <motion.div
                    key={campaign.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                  >
                    <Card className="hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-4 flex-1">
                          <div className="w-12 h-12 rounded-lg bg-mint-100 flex items-center justify-center flex-shrink-0">
                            <Mail className="w-6 h-6 text-mint-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold text-brown-900 truncate">
                                {campaign.name}
                              </h3>
                              <Badge variant={statusColors[campaign.status]}>
                                {CAMPAIGN_STATUS_LABELS[campaign.status]}
                              </Badge>
                              <Badge className={`text-xs`}>
                                {CAMPAIGN_TYPE_LABELS[campaign.type]}
                              </Badge>
                            </div>
                            <p className="text-sm text-brown-600 truncate mb-2">
                              {campaign.subject}
                            </p>
                            <div className="flex items-center gap-4 text-xs text-brown-500">
                              <span className="flex items-center gap-1">
                                <Users className="w-3 h-3" />
                                {campaign.recipient_count} destinataires
                              </span>
                              {campaign.scheduled_at && (
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  Planifie: {formatDate(campaign.scheduled_at)}
                                </span>
                              )}
                              {campaign.sent_at && (
                                <span className="flex items-center gap-1">
                                  <Send className="w-3 h-3" />
                                  Envoye: {formatDate(campaign.sent_at)}
                                </span>
                              )}
                              {campaign.status === 'sent' && (
                                <>
                                  <span className="flex items-center gap-1">
                                    <Eye className="w-3 h-3" />
                                    {campaign.stats.open_rate.toFixed(1)}% ouvert
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Target className="w-3 h-3" />
                                    {campaign.stats.click_rate.toFixed(1)}% clics
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2">
                          {campaign.status === 'draft' && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate(`/marketing/newsletter/${campaign.id}`)}
                                leftIcon={<Edit2 className="w-4 h-4" />}
                              >
                                Modifier
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleSendCampaign(campaign)}
                                isLoading={sendCampaignMutation.isPending}
                                leftIcon={<Send className="w-4 h-4" />}
                              >
                                Envoyer
                              </Button>
                            </>
                          )}
                          {campaign.status === 'scheduled' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handlePauseCampaign(campaign)}
                              isLoading={pauseCampaignMutation.isPending}
                              leftIcon={<Pause className="w-4 h-4" />}
                            >
                              Pause
                            </Button>
                          )}
                          {campaign.status === 'paused' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleSendCampaign(campaign)}
                              isLoading={sendCampaignMutation.isPending}
                              leftIcon={<Play className="w-4 h-4" />}
                            >
                              Reprendre
                            </Button>
                          )}
                          {campaign.status === 'sending' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleCancelCampaign(campaign)}
                              isLoading={cancelCampaignMutation.isPending}
                              leftIcon={<XCircle className="w-4 h-4" />}
                              className="text-red-600"
                            >
                              Annuler
                            </Button>
                          )}
                          {campaign.status === 'sent' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate(`/marketing/campaign/${campaign.id}/stats`)}
                              leftIcon={<BarChart3 className="w-4 h-4" />}
                            >
                              Stats
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDuplicateCampaign(campaign)}
                            isLoading={duplicateCampaignMutation.isPending}
                            leftIcon={<Copy className="w-4 h-4" />}
                          >
                            Dupliquer
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openDeleteModal(campaign)}
                            leftIcon={<Trash2 className="w-4 h-4" />}
                            className="text-red-600 hover:bg-red-50"
                          >
                            Supprimer
                          </Button>
                        </div>
                      </div>
                    </Card>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          ) : (
            <EmptyState
              icon={<Mail className="w-12 h-12" />}
              title="Aucune campagne"
              description="Creez votre premiere campagne newsletter"
              action={
                <Button
                  leftIcon={<Plus className="w-4 h-4" />}
                  onClick={() => navigate('/marketing/newsletter/new')}
                >
                  Nouvelle campagne
                </Button>
              }
            />
          )}
        </TabsContent>

        {/* Segments Tab */}
        <TabsContent value="segments">
          <div className="flex justify-end mb-4">
            <Button
              leftIcon={<Plus className="w-4 h-4" />}
              onClick={() => navigate('/marketing/segments/new')}
            >
              Nouveau segment
            </Button>
          </div>

          {segmentsLoading ? (
            <div className="flex items-center justify-center h-64">
              <Spinner size="lg" />
            </div>
          ) : segments && segments.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <AnimatePresence>
                {segments.map((segment) => (
                  <motion.div
                    key={segment.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                  >
                    <Card className="h-full">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                            <Target className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-brown-900">{segment.name}</h3>
                            <Badge className={segmentTypeColors[segment.type]} size="sm">
                              {SEGMENT_TYPE_LABELS[segment.type]}
                            </Badge>
                          </div>
                        </div>
                        {segment.is_dynamic && (
                          <Badge variant="blue" size="sm">Dynamique</Badge>
                        )}
                      </div>

                      {segment.description && (
                        <p className="text-sm text-brown-600 mb-4 line-clamp-2">
                          {segment.description}
                        </p>
                      )}

                      <div className="flex items-center justify-between text-sm">
                        <span className="flex items-center gap-1 text-brown-500">
                          <Users className="w-4 h-4" />
                          {segment.contact_count} contacts
                        </span>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/marketing/segments/${segment.id}`)}
                          >
                            Modifier
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openSegmentDeleteModal(segment)}
                            className="text-red-600 hover:bg-red-50"
                          >
                            Supprimer
                          </Button>
                        </div>
                      </div>
                    </Card>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          ) : (
            <EmptyState
              icon={<Target className="w-12 h-12" />}
              title="Aucun segment"
              description="Creez des segments pour cibler vos campagnes"
              action={
                <Button
                  leftIcon={<Plus className="w-4 h-4" />}
                  onClick={() => navigate('/marketing/segments/new')}
                >
                  Nouveau segment
                </Button>
              }
            />
          )}
        </TabsContent>
      </Tabs>

      {/* Delete Campaign Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false)
          setSelectedCampaign(null)
        }}
        title="Supprimer la campagne"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-brown-600">
            Etes-vous sur de vouloir supprimer la campagne{' '}
            <strong>{selectedCampaign?.name}</strong> ? Cette action est irreversible.
          </p>
          <div className="flex justify-end gap-3">
            <Button
              variant="ghost"
              onClick={() => {
                setShowDeleteModal(false)
                setSelectedCampaign(null)
              }}
            >
              Annuler
            </Button>
            <Button
              variant="danger"
              onClick={handleDeleteCampaign}
              isLoading={deleteCampaignMutation.isPending}
            >
              Supprimer
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Segment Modal */}
      <Modal
        isOpen={showSegmentDeleteModal}
        onClose={() => {
          setShowSegmentDeleteModal(false)
          setSelectedSegment(null)
        }}
        title="Supprimer le segment"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-brown-600">
            Etes-vous sur de vouloir supprimer le segment{' '}
            <strong>{selectedSegment?.name}</strong> ? Cette action est irreversible.
          </p>
          <div className="flex justify-end gap-3">
            <Button
              variant="ghost"
              onClick={() => {
                setShowSegmentDeleteModal(false)
                setSelectedSegment(null)
              }}
            >
              Annuler
            </Button>
            <Button
              variant="danger"
              onClick={handleDeleteSegment}
              isLoading={deleteSegmentMutation.isPending}
            >
              Supprimer
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
